import { useState, useEffect, useCallback } from 'react'

import { useHistory } from 'react-router-dom'

interface UseUnsavedChangesWarningProps {
  isDirty: boolean
  isSubmitSuccessful: boolean
  shouldBlock?: boolean
}

interface UseUnsavedChangesWarningReturn {
  openConfirmDialog: boolean
  setOpenConfirmDialog: (value: boolean) => void
  handleConfirmDiscard: () => void
  hasRealDirtyFields: boolean
}

export const useUnsavedChangesWarning = ({
  isDirty,
  isSubmitSuccessful,
  shouldBlock = true,
}: UseUnsavedChangesWarningProps): UseUnsavedChangesWarningReturn => {
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [confirmationDetails, setConfirmationDetails] = useState({
    isConfirmed: false,
    path: '',
  })
  const history = useHistory()

  const hasChanges = isDirty

  const handleGoToIntendedPage = useCallback((location) => history.push(location), [history])

  const handleConfirmDiscard = useCallback(() => {
    setConfirmationDetails((prevState) => ({
      ...prevState,
      isConfirmed: true,
    }))
  }, [])

  useEffect(() => {
    if (confirmationDetails.isConfirmed) {
      handleGoToIntendedPage(confirmationDetails.path)
    }

    if (!shouldBlock) return

    const unblock = history.block((location) => {
      if (isDirty && !isSubmitSuccessful) {
        setOpenConfirmDialog(true)
      } else {
        return
      }

      setConfirmationDetails((prevState) => ({
        ...prevState,
        path: `${location.pathname}${location.search}${location.hash}`,
      }))

      if (confirmationDetails.isConfirmed) {
        return
      }

      return false
    })

    return () => {
      unblock()
    }
  }, [
    handleGoToIntendedPage,
    history,
    isDirty,
    isSubmitSuccessful,
    confirmationDetails.isConfirmed,
    confirmationDetails.path,
    shouldBlock,
  ])

  useEffect(() => {
    if (confirmationDetails.isConfirmed) {
      handleGoToIntendedPage(confirmationDetails.path)
      setConfirmationDetails({
        isConfirmed: false,
        path: '',
      })
    }
  }, [confirmationDetails.isConfirmed, confirmationDetails.path, handleGoToIntendedPage])

  useEffect(() => {
    if (!shouldBlock || !isDirty || isSubmitSuccessful) return

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = true
      return true
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [isDirty, isSubmitSuccessful, shouldBlock])

  return {
    openConfirmDialog,
    setOpenConfirmDialog,
    handleConfirmDiscard,
    hasRealDirtyFields: hasChanges,
  }
}
